/* =========================================================
   Jaiman Toys — Gift with Purchase engine
   Rules live in Admin → Content → Metaobjects → Gift with Purchase.
   After every cart change it reads /cart?view=jtgwp (Liquid works out
   entitlements) and adds / removes / caps gift lines to match.
   v30: gift variants de-duplicated (one gift can serve several rules),
        each gift added in its own request (one failure can't block the rest),
        and a failed add is no longer mistaken for "customer removed the gift".
   ============================================================ */
(() => {
  if (window.__jtGwpLoaded) return;
  window.__jtGwpLoaded = true;

  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const DISMISS_KEY = 'jtGwpDismissed2';
  const TIMER_KEY = 'jtCartTimerStart';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} },
  };
  const getDismissed = () => { try { return JSON.parse(store.get(DISMISS_KEY) || '{}'); } catch (e) { return {}; } };
  const setDismissed = (o) => store.set(DISMISS_KEY, JSON.stringify(o));

  const nativeFetch = window.fetch.bind(window);
  let syncing = false, queued = false, debounce;
  const LAST_KEY = 'jtGwpLastQty2';
  let lastQty = (() => { try { return JSON.parse(store.get(LAST_KEY) || 'null'); } catch (e) { return null; } })();
  let giftVariants = [];       // for decorating cart rows
  let giftWas = {};            // variant → { str, cents } real value of ₹1 gifts

  /* ---- Lock cart +/−/remove while gifts are being synced (prevents clicks on stale rows) ---- */
  const BUSY = 'jt-gwp-busy';
  (function injectBusyCss() {
    const s = document.createElement('style');
    s.textContent = 'html.' + BUSY + ' cart-items, html.' + BUSY + ' cart-drawer-items{pointer-events:none;opacity:.6;transition:opacity .15s}'
      + '.jt-gwp-was{opacity:.6;font-weight:400;margin-right:.35em;text-decoration:line-through}';
    (document.head || document.documentElement).appendChild(s);
  })();
  const lock = () => document.documentElement.classList.add(BUSY);
  const unlock = () => document.documentElement.classList.remove(BUSY);

  const schedule = () => { lock(); clearTimeout(debounce); debounce = setTimeout(sync, 350); };

  /* ---- Watch every cart change made by the theme or apps ---- */
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const p = nativeFetch(input, init);
    if (CART_MUTATION.test(url)) p.then(schedule).catch(() => {});
    return p;
  };
  const origOpen = XMLHttpRequest.prototype.open;
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (m, url) { this._jtUrl = String(url || ''); return origOpen.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function () {
    if (CART_MUTATION.test(this._jtUrl || '')) this.addEventListener('load', schedule);
    return origSend.apply(this, arguments);
  };

  async function post(path, body) {
    const r = await nativeFetch(ROOT + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error('JT GWP: ' + path + ' ' + r.status);
    return r.json();
  }

  async function readState() {
    // NOTE: no "Accept: application/json" header here — with it, Shopify ignores
    // ?view=jtgwp and returns the standard cart JSON instead of our template.
    const r = await nativeFetch(ROOT + 'cart?view=jtgwp&_=' + Date.now(), { headers: { Accept: 'text/html' }, cache: 'no-store' });
    const data = JSON.parse(await r.text());
    if (!data || !Array.isArray(data.giftVariants)) throw new Error('JT GWP: cart.jtgwp template not found on this theme');
    return data;
  }

  async function sync() {
    if (syncing) { queued = true; return; }
    syncing = true;
    try {
      const state = await readState();
      // Same gift can be used by several rules — keep each variant once
      giftVariants = Array.from(new Set((state.giftVariants || []).map(String)));
      giftWas = {};
      (state.rules || []).forEach((r) => {
        if (r.was) giftWas[String(r.variant)] = { str: String(r.was), cents: Number(r.wasCents) || 0 };
      });

      // Entitlement per gift variant (rules sharing a gift are summed, max 10)
      const want = {}, available = {};
      (state.rules || []).forEach((r) => {
        const v = String(r.variant);
        want[v] = Math.min(10, (want[v] || 0) + (Number(r.want) || 0));
        available[v] = available[v] || !!r.available;
      });

      const inCart = {};
      Object.keys(state.inCart || {}).forEach((k) => { inCart[String(k)] = Number(state.inCart[k]) || 0; });
      // All lines of each gift variant (a gift can be split over several lines, e.g. one free + one paid)
      const linesOf = {};
      (state.giftLines || []).forEach((l) => {
        const v = String(l.variant);
        (linesOf[v] = linesOf[v] || []).push(l);
        if (!state.inCart || state.inCart[v] == null) inCart[v] = (inCart[v] || 0) + (Number(l.qty) || 0);
      });

      const dismissed = getDismissed();
      const adds = [], updates = {};

      giftVariants.forEach((v) => {
        const target = want[v] || 0;
        const current = inCart[v] || 0;
        const before = lastQty ? lastQty[v] || 0 : null;

        if (target === 0) {
          delete dismissed[v];                       // reset choice once no longer eligible
          if (current > 0) updates[v] = 0;
          return;
        }
        // Customer deleted a gift they were entitled to → respect it
        if (current === 0 && before > 0) dismissed[v] = true;

        if (current > target) updates[v] = target;
        else if (current < target && !dismissed[v] && available[v]) {
          if (current === 0) adds.push({ id: Number(v), quantity: target });
          else updates[v] = target;
        }
      });
      setDismissed(dismissed);

      let changed = false, updatesOk = false;
      const added = {};
      if (Object.keys(updates).length) {
        try {
          const byKey = {};
          Object.keys(updates).forEach((v) => {
            const lines = (linesOf[v] || []).slice().sort((a, b) => (a.price / a.qty) - (b.price / b.qty));
            if (!lines.length) { byKey[v] = updates[v]; return; }   // fallback: variant id
            lines.forEach((l, i) => { byKey[l.key] = i === 0 ? updates[v] : 0; });
          });
          await post('cart/update.js', { updates: byKey });
          changed = true; updatesOk = true;
        } catch (err) { console.warn(err); }   // never block the customer's cart
      }
      // Add each gift separately so one unavailable gift can't stop the others
      for (const a of adds) {
        try {
          await post('cart/add.js', { items: [a] });
          added[String(a.id)] = a.quantity;
          changed = true;
        } catch (err) { console.warn(err); }
      }

      // Remember resulting gift quantities — only what actually succeeded,
      // so a failed add is never mistaken for the customer removing a gift
      const after = {};
      giftVariants.forEach((v) => {
        let q = inCart[v] || 0;
        if (updatesOk && v in updates) q = updates[v];
        if (v in added) q = added[v];
        after[v] = q;
      });
      lastQty = after;
      store.set(LAST_KEY, JSON.stringify(after));

      if (changed) await refreshUI();
      decorate();
      if (!(changed && /\/cart\/?$/.test(location.pathname))) unlock();
    } catch (err) {
      console.warn('JT GWP sync error', err);
      unlock();
    } finally {
      syncing = false;
      if (queued) { queued = false; schedule(); }
    }
  }

  /* ---- Re-render Dawn's cart drawer + cart icon bubble ---- */
  async function refreshUI() {
    if (/\/cart\/?$/.test(location.pathname)) { location.reload(); return; }
    try {
      const data = await (await nativeFetch(ROOT + '?sections=cart-drawer,cart-icon-bubble')).json();
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      if (data['cart-drawer']) {
        const doc = parse(data['cart-drawer']);
        const fresh = doc.querySelector('.drawer__inner');
        const live = document.querySelector('#CartDrawer .drawer__inner');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
        const fd = doc.querySelector('cart-drawer'), ld = document.querySelector('cart-drawer');
        if (fd && ld) ld.classList.toggle('is-empty', fd.classList.contains('is-empty'));
      }
      if (data['cart-icon-bubble']) {
        const src = parse(data['cart-icon-bubble']).querySelector('.shopify-section');
        const bubble = document.getElementById('cart-icon-bubble');
        if (src && bubble) bubble.innerHTML = src.innerHTML;
      }
      document.dispatchEvent(new CustomEvent('jt:gwp:updated'));
    } catch (err) { console.warn('JT GWP refresh error', err); }
  }

  /* ---- Tag gift rows (FREE GIFT label, no qty stepper) + show real value slashed next to ₹1 ---- */
  function addWas(el, txt) {
    if (!el || el.querySelector('.jt-gwp-was')) return;
    const s = document.createElement('s');
    s.className = 'jt-gwp-was';
    s.textContent = txt;
    el.prepend(s, ' ');
  }

  function decorate() {
    document.querySelectorAll('.cart-item').forEach((row) => {
      const link = row.querySelector('a[href*="variant="]');
      const m = link && link.getAttribute('href').match(/variant=(\d+)/);
      const vid = m ? m[1] : '';
      const isGift = !!(vid && giftVariants.includes(vid));
      row.classList.toggle('jt-gift-line', isGift);

      const was = isGift && giftWas[vid];
      if (!was) return;
      const qtyEl = row.querySelector('input.quantity__input');
      const qty = Math.max(1, Number(qtyEl && qtyEl.value) || 1);
      const lineAmount = (was.cents * qty / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const lineStr = was.str.replace(/[\d,]+(\.\d+)?/, lineAmount);

      const unitEl = Array.from(row.querySelectorAll('.cart-item__details > .product-option'))[0];
      addWas(unitEl, was.str);                                   // per-item price under the title
      row.querySelectorAll('.cart-item__totals span.price').forEach((el) => addWas(el, lineStr));   // line total
    });
  }

  /* ---- Countdown pill (from the cart-rewards header) ---- */
  function tickTimers() {
    const els = document.querySelectorAll('[data-jt-timer]');
    if (!els.length) return;
    const total = (Number(els[0].dataset.minutes) || 10) * 60000;
    let start = Number(store.get(TIMER_KEY));
    const now = Date.now();
    if (!start || now - start >= total) { start = now; store.set(TIMER_KEY, String(start)); }
    const left = Math.max(0, total - (now - start));
    const txt = String(Math.floor(left / 60000)).padStart(2, '0') + ':' + String(Math.floor((left % 60000) / 1000)).padStart(2, '0') + ' mins';
    els.forEach((el) => { el.textContent = txt; });
  }

  function init() {
    tickTimers();
    setInterval(tickTimers, 1000);
    const drawer = document.getElementById('CartDrawer');
    if (drawer) new MutationObserver(decorate).observe(drawer, { childList: true, subtree: true });
    if (Number((window.JTGift || {}).cartCount) > 0) sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
